// packages import
import { yupResolver } from "@hookform/resolvers/yup"
import { Box, Button, CircularProgress, DialogActions, Switch, Typography } from "@mui/material"
import { ChangeEvent, FC, useContext } from "react"
import { Controller, FormProvider, SubmitHandler, useForm } from "react-hook-form"
// component import
import { Alert } from "../common/Alert"
import { CustomController } from "../common/CustomController"
import { CustomModal } from "../common/CustomModal"
// others
import { CreateLawTypeInput, useCreateLawTypeMutation } from "../../../generated"
import { CANCEL } from "../../constants"
import { AppContext } from "../../context"
import { ActionType } from "../../context/AppContextReducer"
import { AddNewLawTypeProps, ErrorException } from "../../interfaceTypes"
import { createLawTypeValidationSchema } from "../../validationSchema"

export const AddNewLawType: FC<AddNewLawTypeProps> = ({ refetch }) => {
  const { isOpen, dispatch } = useContext(AppContext)

  const methods = useForm<CreateLawTypeInput>({
    mode: "all",
    resolver: yupResolver(createLawTypeValidationSchema),

    defaultValues: {
      name: '',
      syncWithOutlook: true
    }
  })

  const { handleSubmit, reset, control, setValue } = methods;

  const [createLawTypeMutation, { loading }] = useCreateLawTypeMutation({
    onError({ graphQLErrors }) {
      if (graphQLErrors) {
        graphQLErrors.map(
          // eslint-disable-next-line
          ({ extensions }) => {
            if (extensions) {
              const { exception } = extensions;
              if (exception) {
                const { response } = exception as ErrorException
                const { response: errorResponse } = response || {}
                if (errorResponse) {
                  const { error } = errorResponse
                  Alert.error(error)
                }
              }
            }
          })
      }
    },

    onCompleted(data) {
      if (data) {
        refetch && refetch()
        Alert.success('Law type created successfully')
        handleClose()
        reset()
      }
    }
  })

  const onSubmit: SubmitHandler<CreateLawTypeInput> = async (data: CreateLawTypeInput) => {
    const { name, syncWithOutlook } = data

    await createLawTypeMutation({
      variables: {
        createLawTypeInput: {
          name,
          syncWithOutlook
        }
      }
    })
  }

  const handleChangeSwitch = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setValue("syncWithOutlook", newValue)
  };

  const handleClose = () => isOpen && dispatch({ type: ActionType.SET_IS_OPEN, isOpen: !isOpen })

  return (
    <CustomModal title='Add New Law Type' isOpen={isOpen} handleClose={handleClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} autoComplete='off'>
          <CustomController
            controllerName='name'
            controllerLabel='Title'
            fieldType='text'
          />
          <Box pt={2}>
            <Typography variant="h5" fontSize='14px'>Sync With Outlook:</Typography>

            <Controller
              name="syncWithOutlook"
              control={control}
              render={({ field: { value } }) => (
                <Switch
                  onChange={handleChangeSwitch}
                  checked={value as boolean}
                />
              )}
            />
          </Box>

          <DialogActions sx={{ padding: '30px 0 0' }}>
            <Button onClick={handleClose} variant='outlined' aria-label={CANCEL}>{CANCEL}</Button>

            <Button type="submit" color='primary' variant="contained" disabled={loading} aria-label='add law type'
              endIcon={loading && <CircularProgress size={20} color="inherit" />}>
              Add Law Type
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </CustomModal>
  )
}